import clsx from "clsx";
import styles from "./difference-matrix.module.scss";
import { MatrixValues } from "./differenceMatrixFields";

/**
 * Explicit rather than `values | undefined`: a failed reference fetch would
 * otherwise render as an untinted grid, reading as "every sample matches".
 */
export type ReferenceState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; values: MatrixValues };

export const referenceValues = (state: ReferenceState) =>
  state.status === "ready" ? state.values : undefined;

interface DifferenceMatrixReferenceProps {
  state: ReferenceState;
  hideSame: boolean;
  onHideSameChange: (hideSame: boolean) => void;
}

const COMPONENT: Record<ReferenceState["status"], JSX.Element> = {
  error: (
    <span
      className={styles.reference__error}
      data-testid="difference-matrix-reference-error"
    >
      The main sample&rsquo;s details could not be loaded, so nothing below is
      being compared against it.
    </span>
  ),
  loading: (
    <span data-testid="difference-matrix-reference-loading">
      Loading the main sample&rsquo;s details&hellip;
    </span>
  ),
  ready: (
    <>
      <span>Compared against the pinned main sample</span>
      <span className={styles["legend-item"]}>
        <span className={styles["legend-item__swatch"]} />
        <span>differs</span>
      </span>
      <span className={styles["legend-item"]}>
        <span className={clsx(styles.delta, styles.gained)}>+2</span>
        <span>gained</span>
      </span>
      <span className={styles["legend-item"]}>
        <span className={clsx(styles.delta, styles.lost)}>−3</span>
        <span>lost</span>
      </span>
      <span className={styles["legend-item"]}>grey = same as main</span>
    </>
  )
};

const DifferenceMatrixReference = ({
  state,
  hideSame,
  onHideSameChange
}: DifferenceMatrixReferenceProps): JSX.Element => (
  <div className={styles.reference} data-testid="difference-matrix-reference">
    {COMPONENT[state.status]}

    <label className={styles.toggle}>
      <input
        type="checkbox"
        checked={hideSame}
        onChange={event => onHideSameChange(event.target.checked)}
        data-testid="difference-matrix-hide-same-toggle"
      />
      <span>Show differences only</span>
    </label>
  </div>
);

export default DifferenceMatrixReference;
